import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Body from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useDeleteTripItemsBasedOnOrderId, useDeleteTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { getOrderId, typeConstToTitle } from 'tripPlanner/utils/index'
import noop from 'lib/function/noop'
import config from 'constants/config'

const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: ${rem(40)};
  justify-content: center;
  gap: ${rem(24)};
`

interface Props {
  item: TripItem
  onDeleted?: (item: TripItem) => void
}

function DeleteTripItem(props: Props) {
  const { item, onDeleted = noop } = props
  const { id, tripId, type } = item

  const { closeModal, goBack } = useModalCallbacks()
  const modalElement = useModalElementContext()
  const close = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])

  const { mutate: deleteTripItemMutate, isLoading } = useDeleteTripItem({
    onSuccess: () => {
      close()
      onDeleted(item)
    },
    onError: () => {},
  })

  const { mutate: deleteTripItemsByOrderIdMutate, isLoading: isLoadingDelete } =
    useDeleteTripItemsBasedOnOrderId({
      onSuccess: close,
      onError: () => {},
    })

  const intl = useIntl()

  const typeTitle = typeConstToTitle(type).toLowerCase()

  const orderId = getOrderId(item)

  const confirmDelete = useCallback(() => {
    if (isLoading || isLoadingDelete) return
    if (!orderId) {
      deleteTripItemMutate({
        tripId,
        tripItemId: id,
      })
    } else {
      deleteTripItemsByOrderIdMutate({
        tripId,
        orderId,
      })
    }
  }, [
    deleteTripItemMutate,
    deleteTripItemsByOrderIdMutate,
    id,
    isLoading,
    isLoadingDelete,
    orderId,
    tripId,
  ])

  const deleteBookingTitle = intl.formatMessage({
    id: '0wiizqfmZ5',
    defaultMessage: 'Delete booking from trip',
    description: 'Modal title when deleting a booking from a trip',
  })

  const deleteItemTitle = intl.formatMessage(
    {
      id: 'QuRLjCuvUx',
      defaultMessage: 'Delete {typeTitle} from trip?',
      description: 'Modal title when deleting a specific item type from a trip',
    },
    { typeTitle },
  )

  const deleteItemConfirmation = intl.formatMessage(
    {
      id: 'AtIyaixD5y',
      defaultMessage: 'Are you sure you want to delete this {typeTitle} from your trip?',
      description: 'Confirmation message when deleting a specific item type from a trip',
    },
    { typeTitle },
  )

  const deleteBookingConfirmation = intl.formatMessage({
    id: 'w5CZ4ygx6r',
    defaultMessage: 'Are you sure you want to delete this booking from your trip? Any other items in the same booking will be deleted.',
    description: 'Confirmation message when deleting a booking with related items from a trip',
  })

  const bookingNotCancelledMessage = useMemo(() => {
    const synonym = config.brandConfig.purchaseSynonym
    if (synonym === 'booking') {
      return intl.formatMessage({
        id: 'zsJvIRVXpS',
        defaultMessage: 'However your booking will not be cancelled, and you’ll still be able to view all your bookings in My Bookings.',
        description: 'Info message explaining the booking is not cancelled, with My Bookings section name',
      })
    }
    if (synonym === 'escape') {
      return intl.formatMessage({
        id: 'fcNfWLlCSY',
        defaultMessage: 'However your booking will not be cancelled, and you’ll still be able to view all your bookings in My Escapes.',
        description: 'Info message explaining the booking is not cancelled, with My Escapes section name',
      })
    }
    return intl.formatMessage({
      id: 'k79XTCQwtU',
      defaultMessage: 'However your booking will not be cancelled, and you’ll still be able to view all your bookings in My Purchases.',
      description: 'Info message explaining the booking is not cancelled, with My Purchases section name',
    })
  }, [intl])

  const deleteButtonLabel = intl.formatMessage({
    id: 'gqImI8yK3L',
    defaultMessage: 'Delete',
    description: 'Delete button label in delete trip item confirmation dialog',
  })

  const cancelButtonLabel = intl.formatMessage({
    id: 'ilfOyVLjaP',
    defaultMessage: 'Cancel',
    description: 'Cancel button label in delete trip item confirmation dialog',
  })

  return (
    <>
      <ModalHeader
        title={orderId ? deleteBookingTitle : deleteItemTitle}
        onBackButtonClick={goBack}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <ModalContent>
          <ContentWrap>
            {!orderId && (
              <Body variant="large" colour="neutral-one">
                {deleteItemConfirmation}
              </Body>
            )}
            {orderId && (
              <>
                <Body variant="large" colour="neutral-one">
                  {deleteBookingConfirmation}
                </Body>
                <Body variant="large" colour="neutral-one">
                  {bookingNotCancelledMessage}
                </Body>
              </>
            )}
            {(isLoading || isLoadingDelete) && <LoadingIndicator floating />}
          </ContentWrap>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: deleteButtonLabel,
          disabled: isLoading || isLoadingDelete,
          onClick: confirmDelete,
        }}
        secondaryActionProps={{
          children: cancelButtonLabel,
          disabled: isLoading || isLoadingDelete,
          onClick: close,
        }}
      />
    </>
  )
}

export default DeleteTripItem
